import { IonLabel } from '@ionic/react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import SearchableSelect from '../components/forms/SearchableSelect';
import { SearchableSelectOptionData } from '../components/forms/SearchableSelectModal';
import { generateTextQueryHandler } from '../services/textSearch';
import { Search } from '../types';

type ExtendedSearchParams<T extends Search.Params> = T & { country: string | null };

type Props<T extends Search.Params> = {
  facetValues: Search.FacetValue[] | undefined;
  searchParams: ExtendedSearchParams<T>;
  setSearchParams: (value: ExtendedSearchParams<T>) => void;
};

function SearchCountry<T extends Search.Params>({
  facetValues,
  searchParams,
  setSearchParams
}: Props<T>) {
  const intl = useIntl();

  const handleSelectOption = useCallback(
    (option: SearchableSelectOptionData | undefined) => {
      setSearchParams({ ...searchParams, country: option ? option.value : null });
    },
    [searchParams, setSearchParams]
  );

  let options: SearchableSelectOptionData[] = [];
  if (facetValues) {
    options = facetValues.map(value => {
      const label = intl.formatMessage(
        { id: 'forms.facet.keyAndCount' },
        { count: value.count, key: value.key }
      );
      return { id: value.key, label, value: value.key };
    });
  }

  // this search option is pretty pointless if there's only one country, as there often is
  if (options.length <= 1) {
    return null;
  }

  return (
    <>
      <IonLabel>
        <FormattedMessage id="models.search.attributes.country" />
      </IonLabel>
      <div className="searchable-select-wrapper-outer">
        <div className="searchable-select-wrapper-inner">
          <SearchableSelect
            defaultOptions={options}
            name="country"
            onSelectOption={handleSelectOption}
            queryHandler={generateTextQueryHandler(options)}
            selectedOption={options.filter(option => option.value === searchParams.country)[0]}
            type="text"
            value={searchParams.country}
          />
        </div>
      </div>
    </>
  );
}

export default SearchCountry;
